// ENG-2277 (HTML Forms): the merge tags in a Form that have no value for this patient and surface,
// so the dialog can say what will print blank before anyone prints it.
import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "components/word-processor-field/merge-tags/merge-tags-constants";
import {
  FORM_CONTEXT_EXTENDED_MENUS,
  FORM_CONTEXT_LEAVES
} from "components/word-processor-field/merge-tags/merge-tags-options/form-context-merge-tags.utils";
import { PATIENT_CURRENT_APPOINTMENT_MENU_OPTIONS } from "components/word-processor-field/merge-tags/merge-tags-options/patient-appointments.utils";
import { PATIENT_INFO_MENU_OPTIONS_WITH_PRONOUNS } from "components/word-processor-field/merge-tags/merge-tags-options/patient-info/patient-info.util";
import { PATIENT_MEDICARE_INSURANCE_POLICY_MENU_OPTIONS } from "components/word-processor-field/merge-tags/merge-tags-options/patient-medicare-insurance-policy.utils";
import { getInvoiceMenu } from "components/word-processor-field/merge-tags/merge-tags-options/patient-selected-invoice.utils";
import type { MergeTagMenuEntry } from "components/word-processor-field/merge-tags/merge-tags-options/signature-merge-tags.utils";
import {
  CURRENT_APPOINTMENT_TAG_PREFIX,
  MERGE_TAGS_OPTIONS
} from "components/word-processor-field/merge-tags/merge-tags.utils";
import { isWildcardTag } from "components/word-processor-field/merge-tags/replace-wildcards.utils";

export type BlankMergeTag = { tag: string; title: string };

const MERGE_TAG = /\{\{(.*?)\}\}/g;

const collectTitles = (
  entries: MergeTagMenuEntry[],
  titles: Map<string, string>
) => {
  for (const entry of entries) {
    if (entry.value) titles.set(entry.value, entry.title);
    if (entry.menu) collectTitles(entry.menu, titles);
  }
  return titles;
};

// Some groups join the picker at runtime, not in MERGE_TAGS_OPTIONS, so their titles are collected here
// too: Medicare and Current Appointment (buildMergeTagsOptions, SN-19877), and a purchase agreement's
// invoice group, with its ENG-2492 tags, and the pronouns version of the patient menu (SN-19938). A flagged
// surface's extended context menus hold tags no base menu does, e.g. Patient.MiddleName (ENG-2493).
let menuTitles: Map<string, string> | undefined;
const titleOf = (tag: string) => {
  menuTitles ??= collectTitles(
    [
      ...(MERGE_TAGS_OPTIONS as MergeTagMenuEntry[]),
      PATIENT_MEDICARE_INSURANCE_POLICY_MENU_OPTIONS,
      PATIENT_CURRENT_APPOINTMENT_MENU_OPTIONS as MergeTagMenuEntry,
      getInvoiceMenu(true) as MergeTagMenuEntry,
      PATIENT_INFO_MENU_OPTIONS_WITH_PRONOUNS as MergeTagMenuEntry,
      ...FORM_CONTEXT_EXTENDED_MENUS
    ],
    new Map(FORM_CONTEXT_LEAVES.map(({ value, title }) => [value, title]))
  );
  return menuTitles.get(tag) ?? tag;
};

// A value prints something if it has text or an image or the sign button (a signature still to sign).
const printsSomething = (value: string | null | undefined) =>
  Boolean(value) &&
  (/<(img|button)\b/i.test(value as string) ||
    (value as string)
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim().length > 0);

export const findBlankMergeTags = (
  text: string,
  getMergeTagValue: (
    mergeTag: string,
    options?: Record<string, unknown>
  ) => string | null | undefined
): BlankMergeTag[] => {
  const tokens = Array.from(
    new Set(Array.from(text.matchAll(MERGE_TAG), ([token]) => token))
  );
  return tokens
    .filter(
      (token) =>
        !isWildcardTag(token) && !printsSomething(getMergeTagValue(token))
    )
    .map((token) => {
      const tag = token.slice(2, -2).split("(")[0].trim();
      return { tag, title: titleOf(tag) };
    });
};

type GetMergeTagValue = (
  mergeTag: string,
  options?: Record<string, unknown>
) => string | null | undefined;

// SN-19877 (D4b): with no appointment today a Current Appointment tag resolves to its own token, so
// the form would print "{{Patient.CurrentAppointment.Time}}" as is. Chart notes keep that token to
// resolve later; a Digital Form is printed now, so here it prints blank, and findBlankMergeTags
// lists it with the others.
export const blankUnresolvedCurrentAppointmentTags =
  (getMergeTagValue: GetMergeTagValue): GetMergeTagValue =>
  (mergeTag, options) => {
    const value = getMergeTagValue(mergeTag, options);
    const text = value?.replace(MERGE_TAG_REPLACED_VALUE_PREFIX, "");
    return text?.startsWith(CURRENT_APPOINTMENT_TAG_PREFIX) ? "" : value;
  };
